import { httpClient } from './http-client';
import type { ApiResponse, PaginatedResponse } from '../types/api';

/**
 * Transactions Service
 * Handles transaction-related API calls for Asset and Debt accounts
 */

interface Transaction {
  id: string;
  account_id: string;
  date: string;
  amount: string;
  amount_base: string;
  transaction_type: string;
  category_id?: string | null;
  /** Resolved category name (same as category_name; kept for older code). */
  category?: string | null;
  /** The source's raw label (Plaid PFC, statement issuer category). */
  plaid_category?: string | null;
  description?: string | null;
  reference_number?: string | null;
  payee_payer?: string | null;
  created_at: string;
  updated_at: string;
}

export interface TransactionWithAccount extends Transaction {
  account_name: string;
  account_type: string;
  account_currency: string;
  /** What the row shows: assigned category, else mapped source label, else "Uncategorised". */
  category_name?: string | null;
  category_color?: string | null;
  category_group_type?: string | null;
}

export interface CreateTransactionRequest {
  account_id: string;
  date: string;
  amount: string;
  transaction_type: string;
  category_id?: string;
  category?: string;
  description?: string;
  reference_number?: string;
  payee_payer?: string;
}

class TransactionsService {
  private static readonly BASE_URL = '/transactions';

  /**
   * Create a new transaction
   */
  static async createTransaction(
    data: CreateTransactionRequest
  ): Promise<ApiResponse<Transaction>> {
    return httpClient.post(`${TransactionsService.BASE_URL}`, data);
  }

  /**
   * Get transactions with filtering and pagination
   */
  static async getTransactions(filter?: {
    account_id?: string;
    start_date?: string;
    end_date?: string;
    transaction_type?: string;
    category?: string;
    category_id?: string;
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<PaginatedResponse<TransactionWithAccount>>> {
    const searchParams = new URLSearchParams();

    if (filter) {
      if (filter.account_id) searchParams.set('account_id', filter.account_id);
      if (filter.start_date) searchParams.set('start_date', filter.start_date);
      if (filter.end_date) searchParams.set('end_date', filter.end_date);
      if (filter.transaction_type) searchParams.set('transaction_type', filter.transaction_type);
      if (filter.category) searchParams.set('category', filter.category);
      if (filter.category_id) searchParams.set('category_id', filter.category_id);
      if (filter.page) searchParams.set('page', filter.page.toString());
      if (filter.per_page) searchParams.set('per_page', filter.per_page.toString());
    }

    const url = searchParams.toString() 
      ? `${TransactionsService.BASE_URL}?${searchParams.toString()}`
      : TransactionsService.BASE_URL;

    const rawResponse = await httpClient.get<any>(url);
    
    // Backend returns: { transactions: [], total: number, page: number, per_page: number }
    if (!rawResponse) {
      return {
        data: {
          data: [],
          total: 0,
          page: 1,
          perPage: 25,
          totalPages: 0,
        },
      };
    }
    
    const responseData = (rawResponse as any).data || rawResponse;
    
    // Wrap it to match the expected ApiResponse<PaginatedResponse<T>> format
    return {
      data: {
        data: responseData.transactions || [],
        total: responseData.total || 0,
        page: responseData.page || 1,
        perPage: responseData.per_page || 25,
        totalPages: responseData.per_page 
          ? Math.ceil((responseData.total || 0) / responseData.per_page) 
          : 0,
      },
    };
  }
  /**
   * Bulk categorize transactions
   */
  static async bulkCategorize(
    transactionIds: string[],
    categoryId: string | null
  ): Promise<ApiResponse<{ updated: number }>> {
    return httpClient.put(`${TransactionsService.BASE_URL}/bulk-categorize`, {
      transaction_ids: transactionIds,
      category_id: categoryId,
    });
  }
}

// Export singleton instance
export const transactionsService = TransactionsService;
